import { useCallback, useMemo } from "react";

import { css, cssMap } from "@compiled/react";
import { useIntl } from "react-intl";

export type SocialShareMenuProps = {
    description: string;
    link: string;
    title: string;
    source?: string | null;
};

type SocialShareActionProps = {
    description: string;
    link: string;
    title: string;
    source: string;
};

type SocialShareButtonProps = {
    variant: "facebook" | "twitter" | "linkedin";
    title: string;
    action: SocialShareActionProps;
};

const SocialShareButtonStyle = cssMap({
    base: {
        display: "inline-block",
        width: 34,
        height: 34,
        overflow: "hidden"
    },
    facebook: {
        background: `no-repeat url("data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%2234px%22%20height%3D%2234px%22%20viewBox%3D%220%200%2034%2034%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%3C!--%20Generator%3A%20Sketch%2048.2%20(47327)%20-%20http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch%20--%3E%20%3Ctitle%3EGroup%2018%3C%2Ftitle%3E%20%3Cdesc%3ECreated%20with%20Sketch.%3C%2Fdesc%3E%20%3Cdefs%3E%3C%2Fdefs%3E%20%3Cg%20id%3D%22Scroll-up%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20transform%3D%22translate(-234.000000%2C%20-677.000000)%22%20opacity%3D%220.6%22%3E%20%3Cg%20id%3D%22Group-12%22%20transform%3D%22translate(-12.000000%2C%20666.000000)%22%20fill%3D%22%23162B4C%22%3E%20%3Cg%20id%3D%22Page-1%22%20transform%3D%22translate(128.000000%2C%2011.000000)%22%3E%20%3Cg%20id%3D%22Group-18%22%20transform%3D%22translate(118.000000%2C%200.000000)%22%3E%20%3Cg%20id%3D%22Group-17%22%3E%20%3Cpath%20d%3D%22M17.0281686%2C1.77702313%20C8.36662718%2C1.77702313%201.31986063%2C8.82355876%201.31986063%2C17.4857143%20C1.31986063%2C26.1478698%208.36662718%2C33.1944054%2017.0281686%2C33.1944054%20C25.6905157%2C33.1944054%2032.7372822%2C26.1478698%2032.7372822%2C17.4857143%20C32.7372822%2C8.82355876%2025.6905157%2C1.77702313%2017.0281686%2C1.77702313%20M17.0281686%2C34%20C7.9227554%2C34%200.514285714%2C26.5917524%200.514285714%2C17.4857143%20C0.514285714%2C8.37967616%207.9227554%2C0.971428571%2017.0281686%2C0.971428571%20C26.1343875%2C0.971428571%2033.5428571%2C8.37967616%2033.5428571%2C17.4857143%20C33.5428571%2C26.5917524%2026.1343875%2C34%2017.0281686%2C34%22%20id%3D%22Fill-1%22%3E%3C%2Fpath%3E%20%3Cpath%20d%3D%22M14.1142857%2C15.5044048%20L15.5714565%2C15.5044048%20L15.5714565%2C14.0407857%20C15.5714565%2C13.3947857%2015.5871166%2C12.3990714%2016.0404761%2C11.782619%20C16.5185001%2C11.1293333%2017.1738746%2C10.6857143%2018.3021837%2C10.6857143%20C20.1406776%2C10.6857143%2020.9142857%2C10.9569048%2020.9142857%2C10.9569048%20L20.5497973%2C13.1883571%20C20.5497973%2C13.1883571%2019.9433605%2C13.0070238%2019.376074%2C13.0070238%20C18.8095704%2C13.0070238%2018.3021837%2C13.2170952%2018.3021837%2C13.802381%20L18.3021837%2C15.5044048%20L20.6261402%2C15.5044048%20L20.4640583%2C17.6844524%20L18.3021837%2C17.6844524%20L18.3021837%2C25.2571429%20L15.5714565%2C25.2571429%20L15.5714565%2C17.6844524%20L14.1142857%2C17.6844524%20L14.1142857%2C15.5044048%20Z%22%20id%3D%22Fill-3%22%3E%3C%2Fpath%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3C%2Fsvg%3E")`
    },
    twitter: {
        background: `no-repeat url("data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%2234px%22%20height%3D%2233px%22%20viewBox%3D%220%200%2034%2033%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%3C!--%20Generator%3A%20Sketch%2048.2%20(47327)%20-%20http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch%20--%3E%20%3Ctitle%3EGroup%2020%3C%2Ftitle%3E%20%3Cdesc%3ECreated%20with%20Sketch.%3C%2Fdesc%3E%20%3Cdefs%3E%20%3Cpolygon%20id%3D%22path-1%22%20points%3D%220%2032.7336223%200%200.103462995%2033.0277256%200.103462995%2033.0277256%2032.7336223%22%3E%3C%2Fpolygon%3E%20%3C%2Fdefs%3E%20%3Cg%20id%3D%22Scroll-up%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20transform%3D%22translate(-116.000000%2C%20-677.000000)%22%20opacity%3D%220.6%22%3E%20%3Cg%20id%3D%22Group-12%22%20transform%3D%22translate(-12.000000%2C%20666.000000)%22%3E%20%3Cg%20id%3D%22Page-1%22%20transform%3D%22translate(128.000000%2C%2011.000000)%22%3E%20%3Cg%20id%3D%22Group-20%22%3E%20%3Cg%20id%3D%22Group-7%22%3E%20%3Cmask%20id%3D%22mask-2%22%20fill%3D%22white%22%3E%20%3Cuse%20xlink%3Ahref%3D%22%23path-1%22%3E%3C%2Fuse%3E%20%3C%2Fmask%3E%20%3Cg%20id%3D%22Clip-6%22%3E%3C%2Fg%3E%20%3Cg%20id%3D%22Group-16%22%20mask%3D%22url(%23mask-2)%22%20fill%3D%22%23000000%22%3E%20%3Cpath%20d%3D%22M16.5142454%2C0.899252599%20C7.85230118%2C0.899252599%200.805534634%2C7.86111835%200.805534634%2C16.4187018%20C0.805534634%2C24.9758874%207.85230118%2C31.9377531%2016.5142454%2C31.9377531%20C25.1757869%2C31.9377531%2032.2221507%2C24.9758874%2032.2221507%2C16.4187018%20C32.2221507%2C7.86111835%2025.1757869%2C0.899252599%2016.5142454%2C0.899252599%20M16.5142454%2C32.7336223%20C7.40842941%2C32.7336223%20-4.02787456e-05%2C25.4148092%20-4.02787456e-05%2C16.4187018%20C-4.02787456e-05%2C7.42259442%207.40842941%2C0.103383408%2016.5142454%2C0.103383408%20C25.6196587%2C0.103383408%2033.0277256%2C7.42259442%2033.0277256%2C16.4187018%20C33.0277256%2C25.4148092%2025.6196587%2C32.7336223%2016.5142454%2C32.7336223%22%20id%3D%22Fill-5%22%3E%3C%2Fpath%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3Cpath%20d%3D%22M23.3142857%2C13.0373843%20C22.7779762%2C13.2712762%2022.2015952%2C13.4290636%2021.5972857%2C13.5003867%20C22.2153571%2C13.1362006%2022.6893333%2C12.5600375%2022.9115476%2C11.8731044%20C22.3335476%2C12.2101957%2021.6948333%2C12.4548459%2021.0124048%2C12.585937%20C20.4688095%2C12.0145553%2019.6908571%2C11.6571429%2018.8307381%2C11.6571429%20C17.1805238%2C11.6571429%2015.8419762%2C12.9752256%2015.8419762%2C14.5997187%20C15.8419762%2C14.8308215%2015.8674762%2C15.0547521%2015.918881%2C15.2703152%20C13.4352619%2C15.1467948%2011.2325476%2C13.9765381%209.75678571%2C12.1954529%20C9.49935714%2C12.6309621%209.35283333%2C13.1362006%209.35283333%2C13.6757061%20C9.35283333%2C14.6957459%209.88064286%2C15.5966483%2010.6820714%2C16.1249971%20C10.193119%2C16.1102543%209.73128571%2C15.9767725%209.32733333%2C15.7576233%20L9.32733333%2C15.7934841%20C9.32733333%2C17.2203445%2010.3582619%2C18.4093285%2011.7263571%2C18.6790812%20C11.4758095%2C18.7480136%2011.2123095%2C18.782679%2010.9386905%2C18.782679%20C10.7460238%2C18.782679%2010.558619%2C18.7651471%2010.3764762%2C18.7304817%20C10.7569524%2C19.899543%2011.8599286%2C20.7506387%2013.1689286%2C20.7733505%20C12.1452857%2C21.5630845%2010.8557143%2C22.0332591%209.45604762%2C22.0332591%20C9.21480952%2C22.0332591%208.97640476%2C22.0201102%208.74285714%2C21.9930154%20C10.0660238%2C22.8269776%2011.6373095%2C23.3142857%2013.3247619%2C23.3142857%20C18.8250714%2C23.3142857%2021.8308333%2C18.8312903%2021.8308333%2C14.941193%20C21.8308333%2C14.813688%2021.8284048%2C14.6861831%2021.8223333%2C14.5610688%20C22.4072143%2C14.1454822%2022.9151905%2C13.6266964%2023.3142857%2C13.0373843%22%20id%3D%22Fill-8%22%20fill%3D%22%23162B4C%22%3E%3C%2Fpath%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3C%2Fsvg%3E")`
    },
    linkedin: {
        background: `no-repeat url("data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%2234px%22%20height%3D%2234px%22%20viewBox%3D%220%200%2034%2034%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%3C!--%20Generator%3A%20Sketch%2048.2%20(47327)%20-%20http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch%20--%3E%20%3Ctitle%3EGroup%2019%3C%2Ftitle%3E%20%3Cdesc%3ECreated%20with%20Sketch.%3C%2Fdesc%3E%20%3Cdefs%3E%3C%2Fdefs%3E%20%3Cg%20id%3D%22Scroll-up%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20transform%3D%22translate(-194.000000%2C%20-677.000000)%22%20opacity%3D%220.6%22%3E%20%3Cg%20id%3D%22Group-12%22%20transform%3D%22translate(-12.000000%2C%20666.000000)%22%20fill%3D%22%23162B4C%22%3E%20%3Cg%20id%3D%22Page-1%22%20transform%3D%22translate(128.000000%2C%2011.000000)%22%3E%20%3Cg%20id%3D%22Group-19%22%20transform%3D%22translate(78.000000%2C%200.000000)%22%3E%20%3Cpath%20d%3D%22M17.2%2C1.94285714%20C8.62950916%2C1.94285714%201.65714286%2C8.91522344%201.65714286%2C17.4857143%20C1.65714286%2C26.0562051%208.62950916%2C33.0285714%2017.2%2C33.0285714%20C25.7700923%2C33.0285714%2032.7428571%2C26.0562051%2032.7428571%2C17.4857143%20C32.7428571%2C8.91522344%2025.7700923%2C1.94285714%2017.2%2C1.94285714%20M17.2%2C34%20C8.09418397%2C34%200.685714286%2C26.5915303%200.685714286%2C17.4857143%20C0.685714286%2C8.37989826%208.09418397%2C0.971428571%2017.2%2C0.971428571%20C26.305816%2C0.971428571%2033.7142857%2C8.37989826%2033.7142857%2C17.4857143%20C33.7142857%2C26.5915303%2026.305816%2C34%2017.2%2C34%22%20id%3D%22Fill-10%22%3E%3C%2Fpath%3E%20%3Cpath%20d%3D%22M24%2C23.3142857%20L20.7785%2C23.3142857%20L20.7785%2C18.5326632%20C20.7785%2C17.2813514%2020.2757857%2C16.42796%2019.1679524%2C16.42796%20C18.3207857%2C16.42796%2017.8500476%2C17.0034648%2017.6302619%2C17.5604712%20C17.546881%2C17.7594314%2017.5606429%2C18.0360848%2017.5606429%2C18.3147935%20L17.5606429%2C23.3142857%20L14.3699048%2C23.3142857%20C14.3699048%2C23.3142857%2014.4124048%2C14.8453219%2014.3699048%2C14.0753787%20L17.5606429%2C14.0753787%20L17.5606429%2C15.5244175%20C17.7492619%2C14.8913622%2018.7684524%2C13.9869976%2020.3951905%2C13.9869976%20C22.4137381%2C13.9869976%2024%2C15.3151804%2024%2C18.1770834%20L24%2C23.3142857%20Z%20M11.1435476%2C12.9198473%20L11.1225%2C12.9198473%20C10.0952143%2C12.9198473%209.42857143%2C12.2123875%209.42857143%2C11.3174776%20C9.42857143%2C10.4069468%2010.1146429%2C9.71428571%2011.1641905%2C9.71428571%20C12.2109048%2C9.71428571%2012.854881%2C10.4053025%2012.8759286%2C11.3158333%20C12.8759286%2C12.2107432%2012.2109048%2C12.9198473%2011.1435476%2C12.9198473%20Z%20M9.79488095%2C23.3142857%20L12.6367143%2C23.3142857%20L12.6367143%2C14.0753787%20L9.79488095%2C14.0753787%20L9.79488095%2C23.3142857%20Z%22%20id%3D%22Fill-12%22%3E%3C%2Fpath%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3C%2Fg%3E%20%3C%2Fsvg%3E")`
    }
});

const SocialShareButtonActions = {
    facebook: {
        action: "https://www.facebook.com/dialog/feed",
        params: (props: SocialShareActionProps) => ({
            app_id: "743864389089816",
            caption: props.title,
            link: props.link,
            description: props.description,
            picture: "",
            name: props.title
        })
    },
    twitter: {
        action: "https://twitter.com/share",
        params: (props: SocialShareActionProps) => ({
            text: props.description,
            url: props.link,
            via: props.source
        })
    },
    linkedin: {
        action: "https://www.linkedin.com/shareArticle",
        params: (props: SocialShareActionProps) => ({
            mini: "true",
            url: props.link,
            title: props.title,
            summary: props.description,
            source: props.source
        })
    }
};

const SocialShareButton = ({
    title,
    variant,
    action
}: SocialShareButtonProps) => {
    const href = useMemo(() => {
        const actionDescriptor = SocialShareButtonActions[variant];
        const url = new URL(actionDescriptor.action);
        const params = new URLSearchParams(actionDescriptor.params(action));
        for (const [key, value] of params.entries()) {
            url.searchParams.set(key, value);
        }
        return url.href;
    }, [variant, action]);
    const clickHandler = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (typeof window !== "undefined") {
                window.open(
                    href,
                    "",
                    "menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=400,width=600"
                );
                e.preventDefault();
            }
        },
        [href]
    );
    return (
        <a
            css={[SocialShareButtonStyle.base, SocialShareButtonStyle[variant]]}
            target="_blank"
            rel="noopener noreferrer"
            href={href}
            title={title}
            onClick={clickHandler}
        >
            <span style={{ pointerEvents: "none", visibility: "hidden" }}>
                {title}
            </span>
        </a>
    );
};

const SocialShareMenuStyle = css({
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    alignItems: "center",
    li: {
        height: "34px"
    }
});

export const SocialShareMenu = ({
    description,
    link,
    title,
    source
}: SocialShareMenuProps) => {
    const intl = useIntl();

    const action: SocialShareActionProps = useMemo(
        () => ({
            title,
            description,
            link,
            source: source ?? "Atlassian"
        }),
        [description, link, title, source]
    );

    return (
        <ul css={SocialShareMenuStyle}>
            <li>
                <SocialShareButton
                    variant="facebook"
                    action={action}
                    title={intl.formatMessage({
                        id: "Agile.share-menu.facebook",
                        defaultMessage: "Share on Facebook"
                    })}
                />
            </li>
            <li>
                <SocialShareButton
                    variant="twitter"
                    action={action}
                    title={intl.formatMessage({
                        id: "Agile.share-menu.twitter",
                        defaultMessage: "Share on Twitter"
                    })}
                />
            </li>
            <li>
                <SocialShareButton
                    variant="linkedin"
                    action={action}
                    title={intl.formatMessage({
                        id: "Agile.share-menu.linkedin",
                        defaultMessage: "Share on LinkedIn"
                    })}
                />
            </li>
        </ul>
    );
};
